// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import GoogleAddressField from "@conklin/App/Components/AddressField/GoogleAddressField";
import useLoadPlaces from "@conklin/App/Hooks/useLoadPlaces";
import { GoogleAutoCompleteAddress } from "@conklin/App/Services/GooglePlaceAutocompleteService";
import Checkbox, { CheckboxPresentationProps, CheckboxProps } from "@conklin/MobiusOverrides/Checkbox/Checkbox";
import { xValidateByKey } from "@conklin/XDL/Utilities";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import {
    AddressFieldDisplayCollectionModel,
    BaseAddressModel,
    BillToModel,
    CountryModel,
    CustomerValidationDto,
    ShipToModel,
    StateModel,
} from "@insite/client-framework/Types/ApiModels";
import { ButtonPresentationProps } from "@insite/mobius/Button";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Select, { SelectPresentationProps, SelectProps } from "@insite/mobius/Select";
import TextField, { TextFieldPresentationProps, TextFieldProps } from "@insite/mobius/TextField";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { useEffect, useState } from "react";
import { css } from "styled-components";

interface OwnProps {
    firstName: string;
    lastName: string;
    attention: string;
    companyName: string;
    address1: string;
    address2: string;
    address3: string;
    address4: string;
    country: BaseAddressModel["country"];
    state: BaseAddressModel["state"];
    city: string;
    postalCode: string;
    phone: string;
    email: string;
    fax: string;
    onChangeFirstName: TextFieldProps["onChange"];
    onChangeLastName: TextFieldProps["onChange"];
    onChangeAttention: TextFieldProps["onChange"];
    onChangeCompanyName: TextFieldProps["onChange"];
    onChangeAddress1: TextFieldProps["onChange"];
    onChangeAddress2: TextFieldProps["onChange"];
    onChangeAddress3: TextFieldProps["onChange"];
    onChangeAddress4: TextFieldProps["onChange"];
    onChangeCountry: (event: React.ChangeEvent<HTMLSelectElement>, country: BaseAddressModel["country"]) => void;
    onChangeState: (event: React.ChangeEvent<HTMLSelectElement>, state: BaseAddressModel["state"]) => void;
    onChangeCity: TextFieldProps["onChange"];
    onChangePostalCode: TextFieldProps["onChange"];
    onChangePhone: TextFieldProps["onChange"];
    onChangeEmail: TextFieldProps["onChange"];
    onChangeFax: TextFieldProps["onChange"];
    firstNameError: React.ReactNode;
    lastNameError: React.ReactNode;
    attentionError: React.ReactNode;
    companyNameError: React.ReactNode;
    address1Error: React.ReactNode;
    address2Error: React.ReactNode;
    address3Error: React.ReactNode;
    address4Error: React.ReactNode;
    countryError: React.ReactNode;
    stateError: React.ReactNode;
    cityError: React.ReactNode;
    postalCodeError: React.ReactNode;
    phoneError: React.ReactNode;
    emailError: React.ReactNode;
    faxError: React.ReactNode;
    countries: CountryModel[];
    fieldDisplay: AddressFieldDisplayCollectionModel;
    validation: CustomerValidationDto;
    extendedStyles?: CustomerAddressFormFieldStyles;
    formId: string;
    hideAdditionalAddresses?: boolean;
    // XNG-Changes
    addressUpdate?: (address: Partial<ShipToModel | BillToModel>) => void;
    onChangeTaxExempt?: CheckboxProps["onChange"];
    taxExempt?: boolean;
    disabledTaxExempt?: boolean;
    hideTaxExempt?: boolean;
    formDisabled?: boolean;
    taxExemption?: string;
    onChangeTaxExemption?: TextFieldProps["onChange"];
}

type Props = OwnProps;

export interface CustomerAddressFormFieldStyles {
    form?: InjectableCss;
    container?: GridContainerProps;
    firstNameGridItem?: GridItemProps;
    firstNameText?: TextFieldPresentationProps;
    lastNameGridItem?: GridItemProps;
    lastNameText?: TextFieldPresentationProps;
    companyNameGridItem?: GridItemProps;
    companyNameText?: TextFieldPresentationProps;
    attentionGridItem?: GridItemProps;
    attentionText?: TextFieldPresentationProps;
    address1GridItem?: GridItemProps;
    address1Text?: TextFieldPresentationProps;
    address2GridItem?: GridItemProps;
    address2Text?: TextFieldPresentationProps;
    address3GridItem?: GridItemProps;
    address3Text?: TextFieldPresentationProps;
    address4GridItem?: GridItemProps;
    address4Text?: TextFieldPresentationProps;
    addAddressLineLinkGridItem?: GridItemProps;
    addAddressLineLink?: LinkPresentationProps;
    countryGridItem?: GridItemProps;
    countrySelect?: SelectPresentationProps;
    cityGridItem?: GridItemProps;
    cityText?: TextFieldPresentationProps;
    stateGridItem?: GridItemProps;
    stateSelect?: SelectPresentationProps;
    postalCodeGridItem?: GridItemProps;
    postalCodeText?: TextFieldPresentationProps;
    phoneGridItem?: GridItemProps;
    phoneText?: TextFieldPresentationProps;
    faxGridItem?: GridItemProps;
    faxText?: TextFieldPresentationProps;
    emailGridItem?: GridItemProps;
    emailText?: TextFieldPresentationProps;
    formButtonsWrapper?: InjectableCss;
    cancelButton?: ButtonPresentationProps;
    submitButton?: ButtonPresentationProps;
    // XNG-Changes
    taxExemptGridItem?: GridItemProps;
    taxExempt?: CheckboxPresentationProps;
    taxExmptionCertificateField?: TextFieldPresentationProps;
    taxExmptionWrapper?: InjectableCss;
}

export const customerAddressFormFieldStyles: CustomerAddressFormFieldStyles = {
    firstNameGridItem: { width: 6 },
    lastNameGridItem: { width: 6 },
    companyNameGridItem: { width: 6 },
    attentionGridItem: { width: 6 },
    address1GridItem: { width: 12 },
    address2GridItem: { width: 12 },
    address3GridItem: { width: 12 },
    address4GridItem: { width: 12 },
    addAddressLineLinkGridItem: { width: 12 },
    countryGridItem: { width: 12 },
    cityGridItem: { width: 12 },
    stateGridItem: { width: 6 },
    postalCodeGridItem: { width: 6 },
    phoneGridItem: { width: 6 },
    faxGridItem: { width: 6 },
    emailGridItem: { width: 12 },
    formButtonsWrapper: {
        css: css`
            display: flex;
            justify-content: flex-end;
            margin-top: 30px;
        `,
    },
    cancelButton: {
        color: "secondary",
        css: css`
            margin-right: 10px;
        `,
    },
};

const CustomerAddressFormFields = ({
    firstName,
    lastName,
    attention,
    companyName,
    address1,
    address2,
    address3,
    address4,
    country,
    state,
    city,
    postalCode,
    phone,
    email,
    fax,
    onChangeFirstName,
    onChangeLastName,
    onChangeAttention,
    onChangeCompanyName,
    onChangeAddress1,
    onChangeAddress2,
    onChangeAddress3,
    onChangeAddress4,
    onChangeCountry,
    onChangeState,
    onChangeCity,
    onChangePostalCode,
    onChangePhone,
    onChangeEmail,
    onChangeFax,
    firstNameError,
    lastNameError,
    attentionError,
    companyNameError,
    address1Error,
    address2Error,
    address3Error,
    address4Error,
    countryError,
    stateError,
    cityError,
    postalCodeError,
    phoneError,
    emailError,
    faxError,
    countries,
    fieldDisplay,
    validation,
    extendedStyles,
    formId,
    hideAdditionalAddresses,
    // XNG-Changes
    addressUpdate,
    onChangeTaxExempt,
    taxExempt,
    disabledTaxExempt,
    hideTaxExempt = false,
    formDisabled = false,
    taxExemption,
    onChangeTaxExemption,
}: Props) => {
    const [styles] = React.useState(() => mergeToNew(customerAddressFormFieldStyles, extendedStyles));

    // XNG-Changes
    useLoadPlaces();
    // XNG-Changes: to show autocomplete inside modals
    useEffect(() => {
        document.querySelectorAll(".pac-container").forEach(element => {
            (element as HTMLElement).style.setProperty("z-index", "2000", "important");
        });
    }, [address1, address2, address3, address4]);

    if (!countries) {
        return null;
    }

    const [selectedCountry, setSelectedCountry] = useState<CountryModel | undefined>(
        countries.find(c => c.id === country?.id),
    );
    const [states, setStates] = useState<StateModel[]>(selectedCountry?.states || []);
    const [isAddress2Opened, setIsAddress2Opened] = useState(false);
    const [isAddress3Opened, setIsAddress3Opened] = useState(false);
    const [isAddress4Opened, setIsAddress4Opened] = useState(false);

    useEffect(() => {
        if (countries?.length === 1 && !country) {
            onChangeCountry({} as any, countries[0]);
        }
    }, []);

    useEffect(() => {
        setSelectedCountry(
            country ? countries.find(c => c.id === country?.id) : countries?.length === 1 ? countries[0] : undefined,
        );
    }, [country]);

    useEffect(() => {
        setStates(selectedCountry?.states || []);
    }, [selectedCountry]);

    useEffect(() => {
        if (address2Error || address3Error || address4Error) {
            setIsAddress2Opened(true);
        }
        if (address3Error || address4Error) {
            setIsAddress3Opened(true);
        }
        if (address4Error) {
            setIsAddress4Opened(true);
        }
    }, [address2Error, address3Error, address4Error]);

    const handleCountryChange: SelectProps["onChange"] = event => {
        const country = countries.find(c => c.id === event.target.value);
        setSelectedCountry(country);
        onChangeCountry(event, country || null);
    };

    const handleStateChange: SelectProps["onChange"] = event => {
        const state = selectedCountry?.states?.find(s => s.id === event.target.value);
        onChangeState(event, state || null);
    };

    // XNG-Changes
    const handleAddress1Select = (address: GoogleAutoCompleteAddress) => {
        const selectedCountry = countries.find(c => c.abbreviation === address.countryAbbreviation);
        setSelectedCountry(selectedCountry || undefined);
        const state = selectedCountry?.states?.find(s => s.abbreviation === address.stateAbbreviation) || null;

        const addressToUpdate = {
            address1: address.address1,
            city: address.locality || "",
            postalCode: address.postalCode || "",
            country: !fieldDisplay.country!.isVisible ? null : selectedCountry,
            state: !selectedCountry?.states?.length ? null : state,
        };

        addressUpdate?.(addressToUpdate);
        if (formId) {
            setTimeout(() => {
                xValidateByKey(formId, ["City", "Postal Code"]);
            }, 500);
        }
    };

    // XNG-Changes
    const handleAddress2Select = (address: GoogleAutoCompleteAddress) => {
        const addressToUpdate = { address2: address.address1 };
        addressUpdate?.(addressToUpdate);
    };
    const handleAddress3Select = (address: GoogleAutoCompleteAddress) => {
        const addressToUpdate = { address3: address.address1 };
        addressUpdate?.(addressToUpdate);
    };
    const handleAddress4Select = (address: GoogleAutoCompleteAddress) => {
        const addressToUpdate = { address4: address.address1 };
        addressUpdate?.(addressToUpdate);
    };

    return (
        <GridContainer {...styles.container} data-test-selector="customerAddressFormFields">
            {fieldDisplay.firstName!.isVisible && (
                <GridItem {...styles.firstNameGridItem}>
                    <TextField
                        {...styles.firstNameText}
                        name="firstName"
                        label={fieldDisplay.firstName!.displayName}
                        value={firstName}
                        // XNG-Changes: add formDisabled
                        disabled={validation.firstName!.isDisabled || formDisabled}
                        required={validation.firstName!.isRequired}
                        onChange={onChangeFirstName}
                        error={firstNameError}
                        data-test-selector="customerAddressForm_firstName"
                        // XNG-Changes
                        type="firstName"
                        {...{ formId }}
                    />
                </GridItem>
            )}
            {fieldDisplay.lastName!.isVisible && (
                <GridItem {...styles.lastNameGridItem}>
                    <TextField
                        {...styles.lastNameText}
                        name="lastName"
                        label={fieldDisplay.lastName!.displayName}
                        value={lastName}
                        // XNG-Changes: add formDisabled
                        disabled={validation.lastName!.isDisabled || formDisabled}
                        required={validation.lastName!.isRequired}
                        onChange={onChangeLastName}
                        error={lastNameError}
                        data-test-selector="customerAddressForm_lastName"
                        // XNG-Changes
                        type="lastName"
                        {...{ formId }}
                    />
                </GridItem>
            )}
            {fieldDisplay.companyName!.isVisible && (
                <GridItem {...styles.companyNameGridItem}>
                    <TextField
                        {...styles.companyNameText}
                        name="companyName"
                        label={fieldDisplay.companyName!.displayName}
                        value={companyName}
                        // XNG-Changes: add formDisabled
                        disabled={validation.companyName!.isDisabled || formDisabled}
                        required={validation.companyName!.isRequired}
                        onChange={onChangeCompanyName}
                        error={companyNameError}
                        data-test-selector="customerAddressForm_companyName"
                        // XNG-Changes
                        type="companyName"
                        {...{ formId }}
                    />
                </GridItem>
            )}
            {fieldDisplay.country!.isVisible && countries.length > 1 && (
                <GridItem {...styles.countryGridItem}>
                    <Select
                        {...styles.countrySelect}
                        label={fieldDisplay.country!.displayName}
                        value={selectedCountry?.id || ""}
                        // XNG-Changes: add formDisabled
                        disabled={validation.country!.isDisabled || countries.length === 1 || formDisabled}
                        required={validation.country!.isRequired}
                        onChange={handleCountryChange}
                        error={countryError}
                        data-test-selector="customerAddressForm_country"
                    >
                        <option value="Select Country">{translate("Select Country")}</option>
                        {countries.map(co => (
                            <option key={co.id} value={co.id}>
                                {co.name}
                            </option>
                        ))}
                    </Select>
                </GridItem>
            )}
            {fieldDisplay.attention!.isVisible && (
                <GridItem {...styles.attentionGridItem}>
                    <TextField
                        {...styles.attentionText}
                        name="attention"
                        label={fieldDisplay.attention!.displayName}
                        value={attention}
                        // XNG-Changes: add formDisabled
                        disabled={validation.attention!.isDisabled || formDisabled}
                        required={validation.attention!.isRequired}
                        onChange={onChangeAttention}
                        error={attentionError}
                        data-test-selector="customerAddressForm_attention"
                    />
                </GridItem>
            )}
            {fieldDisplay.address1!.isVisible && (
                <GridItem {...styles.address1GridItem}>
                    {/* XNG-Changes: google address */}
                    <GoogleAddressField
                        {...styles.address1Text}
                        name="address1"
                        label={fieldDisplay.address1!.displayName}
                        value={address1}
                        // XNG-Changes: add formDisabled
                        disabled={validation.address1!.isDisabled || formDisabled}
                        required={validation.address1!.isRequired}
                        onChange={onChangeAddress1}
                        error={address1Error}
                        data-test-selector="customerAddressForm_address1"
                        // XNG-Changes
                        type="address"
                        {...{ formId }}
                        onAddressSelected={address => handleAddress1Select(address)}
                        provider="google"
                        placeholder={fieldDisplay.address1!.displayName || translate("Address1")}
                    />
                </GridItem>
            )}
            <>
                {/** XNG-Changes
                {hideAdditionalAddresses && !isAddress2Opened && (
                    <GridItem {...styles.addAddressLineLinkGridItem}>
                        <Link
                            {...styles.addAddressLineLink}
                            onClick={() => setIsAddress2Opened(true)}
                            data-test-selector="customerAddressForm_addressLine2"
                        >
                            {translate("+ Address line 2")}
                        </Link>
                    </GridItem>
                )}
                */}
                {/** XNG-Changes: remove !hideAdditionalAddresses || isAddress2Opened */}
                <GridItem {...styles.address2GridItem}>
                    {/* XNG-Changes: google address */}
                    <GoogleAddressField
                        {...styles.address2Text}
                        name="address2"
                        label={fieldDisplay.address2!.displayName}
                        value={address2}
                        // XNG-Changes: add formDisabled
                        disabled={validation.address2!.isDisabled || formDisabled}
                        required={validation.address2!.isRequired}
                        // maxLength={validation.address2!.maxLength || undefined}
                        onChange={onChangeAddress2}
                        error={address2Error}
                        data-test-selector="customerAddressForm_address2"
                        // XNG-Changes
                        onAddressSelected={address => {
                            handleAddress2Select(address);
                        }}
                        provider="google"
                        type="address"
                        placeholder={fieldDisplay.address2!.displayName || translate("address2")}
                        {...{ formId }}
                    />
                </GridItem>
            </>
            <>
                {/* XNG-Changes: isVisible */}
                {hideAdditionalAddresses && isAddress2Opened && !isAddress3Opened && fieldDisplay.address3!.isVisible && (
                    <GridItem {...styles.addAddressLineLinkGridItem}>
                        <Link {...styles.addAddressLineLink} onClick={() => setIsAddress3Opened(true)}>
                            {translate("+ Address line 3")}
                        </Link>
                    </GridItem>
                )}
                {/* XNG-Changes: isVisible */}
                {(!hideAdditionalAddresses || isAddress3Opened) && fieldDisplay.address3!.isVisible && (
                    <GridItem {...styles.address3GridItem}>
                        {/* XNG-Changes: google address */}
                        <GoogleAddressField
                            {...styles.address3Text}
                            name="address3"
                            label={fieldDisplay.address3!.displayName}
                            value={address3}
                            // XNG-Changes: add formDisabled
                            disabled={validation.address3!.isDisabled || formDisabled}
                            required={validation.address3!.isRequired}
                            // maxLength={validation.address3!.maxLength || undefined}
                            onChange={onChangeAddress3}
                            error={address3Error}
                            data-test-selector="customerAddressForm_address3"
                            // XNG-Changes
                            onAddressSelected={address => {
                                handleAddress3Select(address);
                            }}
                            provider="google"
                            type="address"
                            placeholder={fieldDisplay.address3!.displayName || translate("address3")}
                            {...{ formId }}
                        />
                    </GridItem>
                )}
            </>
            <>
                {/* XNG-Changes: isVisible */}
                {hideAdditionalAddresses && isAddress3Opened && !isAddress4Opened && fieldDisplay.address4!.isVisible && (
                    <GridItem {...styles.addAddressLineLinkGridItem}>
                        <Link {...styles.addAddressLineLink} onClick={() => setIsAddress4Opened(true)}>
                            {translate("+ Address line 4")}
                        </Link>
                    </GridItem>
                )}
                {/* XNG-Changes: isVisible */}
                {(!hideAdditionalAddresses || isAddress4Opened) && fieldDisplay.address4!.isVisible && (
                    <GridItem {...styles.address4GridItem}>
                        {/* XNG-Changes: google address */}
                        <GoogleAddressField
                            {...styles.address4Text}
                            name="address4"
                            label={fieldDisplay.address4!.displayName}
                            value={address4}
                            // XNG-Changes: add formDisabled
                            disabled={validation.address4!.isDisabled || formDisabled}
                            required={validation.address4!.isRequired}
                            // maxLength={validation.address4!.maxLength || undefined}
                            onChange={onChangeAddress4}
                            error={address4Error}
                            data-test-selector="customerAddressForm_address4"
                            // XNG-Changes
                            onAddressSelected={address => {
                                handleAddress4Select(address);
                            }}
                            provider="google"
                            type="address"
                            placeholder={fieldDisplay.address4!.displayName || translate("address4")}
                            {...{ formId }}
                        />
                    </GridItem>
                )}
            </>

            {fieldDisplay.city!.isVisible && (
                <GridItem {...styles.cityGridItem}>
                    <TextField
                        {...styles.cityText}
                        name="city"
                        label={fieldDisplay.city!.displayName}
                        value={city}
                        // XNG-Changes: add formDisabled
                        disabled={validation.city!.isDisabled || formDisabled}
                        required={validation.city!.isRequired}
                        onChange={onChangeCity}
                        data-test-selector="customerAddressForm_city"
                        // XNG-Changes
                        type="city"
                        {...{ formId }}
                    />
                </GridItem>
            )}
            {/* XNG-Changes: remove state.length > 0 */}
            <GridItem {...styles.stateGridItem}>
                <Select
                    {...styles.stateSelect}
                    label={fieldDisplay.state!.displayName}
                    value={state?.id || ""}
                    // XNG-Changes: add formDisabled
                    disabled={validation.state!.isDisabled || formDisabled || states.length === 0}
                    required
                    onChange={handleStateChange}
                    error={stateError}
                    data-test-selector="customerAddressForm_state"
                >
                    <option value="Select State">{translate("Select State")}</option>
                    {states.map(st => (
                        <option key={st.id} value={st.id}>
                            {st.name}
                        </option>
                    ))}
                </Select>
            </GridItem>
            {fieldDisplay.postalCode!.isVisible && (
                <GridItem {...styles.postalCodeGridItem}>
                    <TextField
                        {...styles.postalCodeText}
                        name="postalCode"
                        label={fieldDisplay.postalCode!.displayName}
                        value={postalCode}
                        // XNG-Changes: add formDisabled
                        disabled={validation.postalCode!.isDisabled || formDisabled}
                        required={validation.postalCode!.isRequired}
                        onChange={onChangePostalCode}
                        data-test-selector="customerAddressForm_postalCode"
                        // XNG-Changes
                        type={selectedCountry?.name === "Canada" ? "postalCodeCA" : "postalCodeUS"}
                        {...{ formId }}
                    />
                </GridItem>
            )}
            {fieldDisplay.phone!.isVisible && (
                <GridItem {...styles.phoneGridItem}>
                    <TextField
                        {...styles.phoneText}
                        name="phone"
                        label={fieldDisplay.phone!.displayName}
                        value={phone}
                        // XNG-Changes: add formDisabled
                        disabled={validation.phone!.isDisabled || formDisabled}
                        required={validation.phone!.isRequired}
                        onChange={onChangePhone}
                        data-test-selector="customerAddressForm_phone"
                        placeholder="(___) ___ - ____"
                        type="phone"
                        {...{ formId }}
                    />
                </GridItem>
            )}
            {fieldDisplay.fax!.isVisible && (
                <GridItem {...styles.faxGridItem}>
                    <TextField
                        {...styles.faxText}
                        name="fax"
                        label={fieldDisplay.fax!.displayName}
                        value={fax}
                        // XNG-Changes: add formDisabled
                        disabled={validation.fax!.isDisabled || formDisabled}
                        required={validation.fax!.isRequired}
                        onChange={onChangeFax}
                        data-test-selector="customerAddressForm_fax"
                    />
                </GridItem>
            )}
            {fieldDisplay.email!.isVisible && (
                <GridItem {...styles.emailGridItem}>
                    <TextField
                        {...styles.emailText}
                        name="email"
                        label={fieldDisplay.email!.displayName}
                        value={email}
                        // XNG-Changes: add formDisabled
                        disabled={validation.email!.isDisabled || formDisabled}
                        required={validation.email!.isRequired}
                        onChange={onChangeEmail}
                        data-test-selector="customerAddressForm_email"
                        // XNG-Changes
                        type="email"
                        {...{ formId }}
                    />
                </GridItem>
            )}
            {/** XNG-Changes */}
            {!hideTaxExempt && (
                <GridItem {...styles.taxExemptGridItem}>
                    <Checkbox
                        {...styles.taxExempt}
                        checked={taxExempt}
                        disabled={disabledTaxExempt}
                        onChange={onChangeTaxExempt}
                    >
                        {translate("Tax Exempt")}
                    </Checkbox>
                    <StyledWrapper {...{ taxExempt }} {...styles.taxExmptionWrapper}>
                        <TextField
                            required={taxExempt}
                            type="taxExempt"
                            label={translate("Tax Exempt Certificate Number")}
                            value={taxExemption}
                            onChange={onChangeTaxExemption}
                            placeholder={translate("Certificate Number")}
                            {...styles.taxExmptionCertificateField}
                            {...{ formId }}
                        />
                    </StyledWrapper>
                </GridItem>
            )}
        </GridContainer>
    );
};

export default CustomerAddressFormFields;
